import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

const sizeClasses = {
  xs: 'min-h-4 min-w-4 px-0.5 text-[10px]',
  sm: 'min-h-5 min-w-5 px-1 text-3xs',
  md: 'min-h-6 min-w-6 px-1.5 text-2xs',
} as const;

type KbdSize = keyof typeof sizeClasses;

interface KbdProps {
  children: ReactNode;
  size?: KbdSize;
  className?: string;
}

/**
 * Inline keyboard-shortcut token. Replaces hand-rolled `<kbd>` recipes.
 */
export function Kbd({ children, size = 'sm', className }: KbdProps) {
  return (
    <kbd
      className={cn(
        'inline-flex items-center justify-center rounded border border-border/50 bg-muted/40 font-sans leading-none text-muted-foreground',
        sizeClasses[size],
        className,
      )}
    >
      {children}
    </kbd>
  );
}

const KEY_LABELS: Record<string, string> = {
  meta: '⌘',
  cmd: '⌘',
  ctrl: '⌃',
  control: '⌃',
  alt: '⌥',
  option: '⌥',
  shift: '⇧',
  enter: '⏎',
  return: '⏎',
  esc: 'Esc',
  escape: 'Esc',
  tab: '⇥',
  space: '␣',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  backspace: '⌫',
  delete: '⌦',
};

/**
 * Whether the current device uses the Apple modifier conventions (⌘/⌥). Used to
 * resolve the platform-agnostic `mod` key to ⌘ on Apple and `Ctrl` elsewhere.
 * Falls back to non-Apple when there is no `navigator` (e.g. SSR).
 */
function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const source = `${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`;
  return /mac|iphone|ipad|ipod/i.test(source);
}

function keyLabel(key: string): string {
  const k = key.toLowerCase();
  // `mod` is the portable "primary modifier" — ⌘ on Apple, Ctrl everywhere else
  // — so a single shortcut string renders correctly on every platform.
  if (k === 'mod') return isApplePlatform() ? '⌘' : 'Ctrl';
  return KEY_LABELS[k] ?? key.toUpperCase();
}

interface KbdComboProps {
  keys: string[];
  size?: KbdSize;
  className?: string;
  /** Forwarded to every key cap, for callers that want a more prominent look. */
  keyClassName?: string;
}

/**
 * Positioning + sizing for the hold-Alt reveal badge: centered on a control
 * corner (anchored at the corner, then translated by half its own size so the
 * badge's midpoint sits exactly on the corner point) so it pops over the
 * control and partially covers it rather than replacing its content. Shared so
 * the view tabs and the composer buttons reveal their shortcuts the same way.
 * `z-20` keeps it above the control. The keys render as one compact glyph run
 * (⌘⇧U) rather than separate key-caps so a wide combo stays narrow enough not to
 * overlap a neighbouring control in a tight button row.
 */
const hotkeyHintBadgeClassName =
  'pointer-events-none absolute top-0 z-20 -translate-y-1/2 inline-flex items-center gap-0.5 rounded-md border border-border/50 bg-popover px-1 py-0.5 font-sans text-[10px] leading-none text-muted-foreground shadow-sm';

const hotkeyHintBadgeSideClassName = {
  right: 'right-0 translate-x-1/2',
  left: 'left-0 -translate-x-1/2',
} as const;

export type HotkeyHintBadgeSide = keyof typeof hotkeyHintBadgeSideClassName;

interface HotkeyHintBadgeProps {
  /** Keys of the shortcut, e.g. `['mod', '1']`. */
  keys: string[];
  /**
   * Which corner the badge sits on. Default `right` (upper-right). Use `left`
   * when the control is flush with the viewport's right edge so the badge
   * stays on-screen.
   */
  side?: HotkeyHintBadgeSide;
  className?: string;
}

/**
 * Compact glyphs for the reveal badge. Differs from `keyLabel` only for the
 * portable `mod` key: the badge shows the ⌃ glyph on non-Apple rather than the
 * wider "Ctrl" word, so a three-key combo (⌃⇧U) stays narrow enough not to
 * overlap a neighbouring control in a tight button row. The full spelled-out
 * shortcut still lives in the control's tooltip/title.
 */
function hotkeyHintGlyph(key: string): string {
  if (key.toLowerCase() === 'mod') return isApplePlatform() ? '⌘' : '⌃';
  return keyLabel(key);
}

/**
 * The reveal badge itself: a compact key-combo glyph run pinned to a corner
 * of its nearest positioned ancestor, popping over it and partially covering
 * it (never hiding the control's own content). Decorative only —
 * `aria-hidden`, no pointer events — so the marked control keeps its
 * behavior and accessible name. Render it inside a `relative`, non-clipping
 * element: `HotkeyHint` supplies that wrapper, and callers with an
 * already-positioned control (e.g. a tab trigger) can render it directly.
 */
export function HotkeyHintBadge({ keys, side = 'right', className }: HotkeyHintBadgeProps) {
  return (
    <span
      aria-hidden
      data-slot="hotkey-hint"
      className={cn(hotkeyHintBadgeClassName, hotkeyHintBadgeSideClassName[side], className)}
    >
      {keys.map((k, i) => (
        <span key={`${k}-${i}`}>{hotkeyHintGlyph(k)}</span>
      ))}
    </span>
  );
}

interface HotkeyHintProps {
  /** Keys of the shortcut, e.g. `['mod', '.']`. */
  keys: string[];
  /** Whether the badge is showing — typically `useAltKeyHeld()`. */
  show: boolean;
  /** The control the shortcut triggers; the badge pops over its corner. */
  children: ReactNode;
  className?: string;
}

/**
 * Hold-to-reveal shortcut badge: wraps a control and, while `show` is true
 * (pair with `useAltKeyHeld`), pops its key combo over the control's upper-right
 * corner, partially covering it. The badge is decorative — `aria-hidden`, no
 * pointer events — so the wrapped control's behavior and accessible name are
 * untouched. Put the shortcut in the control's tooltip too for non-keyboard
 * discovery.
 */
export function HotkeyHint({ keys, show, children, className }: HotkeyHintProps) {
  return (
    <span className={cn('relative inline-flex', className)}>
      {children}
      {show && <HotkeyHintBadge keys={keys} />}
    </span>
  );
}

/**
 * Renders a set of keys joined with a small gap, e.g. ⌘ + K.
 */
export function KbdCombo({ keys, size = 'sm', className, keyClassName }: KbdComboProps) {
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)}>
      {keys.map((k, i) => (
        <Kbd key={`${k}-${i}`} size={size} className={keyClassName}>
          {keyLabel(k)}
        </Kbd>
      ))}
    </span>
  );
}
